// frontend/src/config/menuSections.js
//
// Single source of truth for the app's navigable pages. Used by the sidebar
// (components/Navigation.js) and the landing dashboard (pages/HomeDashboard.js).
//
// Item flags:
//   key        - page-permission key checked via hasPageAccess()
//   adminOnly  - only shown to staff/admin users
//   alwaysShow - shown regardless of page permissions
//   hidden     - never shown in navigation (route still exists for direct links)
// Section fields:
//   color      - accent colour used by the dashboard cards and sidebar

import React from 'react';
import {
  Dashboard,
  People,
  Settings,
  Assessment,
  Security,
  Business,
  AccountCircle,
  AdminPanelSettings,
  ViewList,
  BarChart,
  Factory,
  Build,
  ShoppingCart,
  CloudUpload,
  Inventory,
  Category,
  AccountTree,
  SyncAlt,
  Upload,
  SwapHoriz,
  LocalShipping,
  CompareArrows,
  Add,
  EventNote,
  Backup,
  History,
  VpnKey,
  FactCheck,
  ReportProblem,
  DeleteSweep
} from '@mui/icons-material';

export const menuSections = [
  {
    title: null, // No title for first section
    color: '#607d8b',
    items: [
      { 
        text: 'Admin Dashboard', 
        icon: <AdminPanelSettings />, 
        path: '/admin-dashboard', 
        key: 'admin-dashboard', 
        adminOnly: true 
      },
      { 
        text: 'Dashboard', 
        icon: <Dashboard />, 
        path: '/dashboard', 
        key: 'dashboard',
        alwaysShow: true  
      },
      { 
        text: 'My Profile', 
        icon: <AccountCircle />, 
        path: '/profile', 
        key: 'profile',
        alwaysShow: true  
      },
    ]
  },
  {
    title: 'Sage Integration',
    color: '#00897b',
    items: [
      { 
        text: 'Sage Import', 
        icon: <CloudUpload />, 
        path: '/manufacturing/sage-import', 
        key: 'sage-import' 
      },
      { 
        text: 'Code Mappings', 
        icon: <SyncAlt />,  
        path: '/manufacturing/sage-mappings', 
        key: 'sage-mappings' 
      },
    ]
  },
  {
    title: 'Orders & Customers',
    color: '#1e88e5',
    items: [
      { 
        text: 'Customer Orders', 
        icon: <ShoppingCart />, 
        path: '/manufacturing/customer-orders', 
        key: 'customer-orders' 
      },
      { 
        text: 'Customers', 
        icon: <Business />, 
        path: '/manufacturing/customers', 
        key: 'customers' 
      },
    ]
  },
  {
    title: 'Dispatch',
    color: '#fb8c00',
    items: [
      { 
        text: 'Dispatch Management', 
        icon: <LocalShipping />, 
        path: '/dispatch', 
        key: 'dispatch' 
      },
    ]
  },
  {
    title: 'Manufacturing',
    color: '#00acc1',
    items: [
      { 
        text: 'Manufacturing', 
        icon: <Factory />, 
        path: '/manufacturing', 
        key: 'manufacturing',
        hidden: true
      },
      { 
        text: 'Work Orders', 
        icon: <ViewList />, 
        path: '/manufacturing/work-orders', 
        key: 'work-orders',
        hidden: true
      },
      { 
        text: 'Create Work Order', 
        icon: <Add />, 
        path: '/manufacturing/create-work-order', 
        key: 'create-work-order',
        hidden: true
      },
      { 
        text: 'Recalculate Orders',
        icon: <SyncAlt />,
        path: '/manufacturing/recalculate',
        key: 'recalculate',
        hidden: true
      },                               
      { 
        text: 'Shop Floor', 
        icon: <Build />, 
        path: '/shop-floor/all', 
        key: 'shop-floor',
        hidden: true
      },
      { 
        text: 'Inventory', 
        icon: <Inventory />, 
        path: '/manufacturing/inventory', 
        key: 'inventory' 
      },
      { 
        text: 'Stock Allocations', 
        icon: <CompareArrows />, 
        path: '/manufacturing/allocations', 
        key: 'allocations' 
      },
      {
        text: 'Stock Conversion',
        icon: <SwapHoriz />,
        path: '/manufacturing/convert',
        key: 'convert'
      },
    ]
  },
  {
    title: 'Manufacturing V2',
    color: '#8e24aa',
    items: [
      {
        text: 'Dashboard',
        icon: <Dashboard />,
        path: '/manufacturing/v2/dashboard',
        key: 'manufacturing-dashboard-v2'
      },
      {
        text: 'KPIs',
        icon: <Assessment />,
        path: '/manufacturing/v2/kpis',
        key: 'kpi-dashboard-v2'
      },
      {
        text: 'Work Orders V2',
        icon: <ViewList />,
        path: '/manufacturing/v2/work-orders',
        key: 'work-orders-v2'
      },
      {
        text: 'Create Work Order V2',
        icon: <Add />,
        path: '/manufacturing/v2/work-orders/new',
        key: 'work-orders-v2-create'
      },
      {
        text: 'Shop Floor V2',
        icon: <Build />,
        // Land on Final Assembly by default — the all-operations view is a
        // click away via the terminal's "All" button.
        path: '/manufacturing/v2/shop-floor/OP-25',
        key: 'shop-floor-v2'
      },
      {
        text: 'Transaction History',
        icon: <History />,
        path: '/manufacturing/v2/history',
        key: 'transaction-history-v2'
      },
      {
        text: 'Capacity Planning',
        icon: <EventNote />,
        path: '/manufacturing/v2/capacity',
        key: 'capacity-planning-v2'
      },
      {
        text: 'WO Creation Log',
        icon: <FactCheck />,
        path: '/manufacturing/v2/wo-creation-log',
        key: 'wo-creation-log'
      },
      {
        text: 'Material Shortages',
        icon: <ReportProblem />,
        path: '/manufacturing/v2/shortages',
        key: 'material-shortages-v2'
      },
      {
        text: 'Scrap',
        icon: <DeleteSweep />,
        path: '/manufacturing/v2/scrap',
        key: 'scrap-v2'
      },
    ]
  },
  {
    title: 'Product Data',
    color: '#43a047',
    items: [
      { 
        text: 'Items', 
        icon: <Category />, 
        path: '/manufacturing/items', 
        key: 'items' 
      },
      { 
        text: 'BOM', 
        icon: <AccountTree />, 
        path: '/manufacturing/bom', 
        key: 'bom' 
      },
      { 
        text: 'BOM Configurator',
        icon: <AccountTree />, 
        path: '/manufacturing/bom-configurator', 
        key: 'bom-configurator',
        hidden: true
      },
      { 
        text: 'BOM Swap', 
        icon: <SwapHoriz />, 
        path: '/manufacturing/bom-swap', 
        key: 'bom-swap' 
      },
      { 
        text: 'Operations', 
        icon: <Build />, 
        path: '/manufacturing/operations', 
        key: 'operations' 
      },
    ]
  },
  {
    title: 'Planning',
    color: '#d81b60',
    items: [
      {
        text: 'Capacity Planning',
        icon: <EventNote />,
        path: '/capacity-planning',
        key: 'capacity-planning'
      },
      {
        text: 'Stock Procurement',
        icon: <ShoppingCart />,
        path: '/stock-procurement',
        key: 'stock-procurement'
      },
    ]
  },
  {
    title: 'Reports & Analytics',
    color: '#f4511e',
    items: [
      { 
        text: 'Analytics', 
        icon: <Assessment />, 
        path: '/analytics', 
        key: 'analytics' 
      },
        { 
        text: 'Data Dashboard', 
        icon: <BarChart />,
        path: '/tv-dashboard', 
        key: 'tv-dashboard' 
      },
      { 
        text: 'Reports', 
        icon: <BarChart />, 
        path: '/reports', 
        key: 'reports' 
      },
      { 
        text: 'Tasks', 
        icon: <ViewList />, 
        path: '/tasks', 
        key: 'tasks' 
      },
    ]
  },
  {
    title: 'Administration',
    color: '#e53935',
    items: [
      {
        text: 'User Management',
        icon: <People />,
        path: '/users',
        key: 'users',
        adminOnly: true
      },
      {
        text: 'Orders API',
        icon: <VpnKey />,
        path: '/orders-api',
        key: 'orders-api',
        adminOnly: true
      },
      { 
        text: 'System Settings', 
        icon: <Settings />, 
        path: '/settings', 
        key: 'settings' 
      },
      {
        text: 'Audit Logs',
        icon: <Security />,
        path: '/audit',
        key: 'audit',
        adminOnly: true
      },
      {
        text: 'Backups',
        icon: <Backup />,
        path: '/admin/backups',
        key: 'backups',
        adminOnly: true
      },
    ]
  },
  {
    title: 'Data Management',
    color: '#6d4c41',
    items: [
      {
        text: 'Import Data',
        icon: <Upload />,
        path: '/import',
        key: 'import'
      },
    ]
  },
];


/**
 * Returns true if the given menu item should be visible to the current user.
 */
export const isMenuItemVisible = (item, { isAdmin, hasPageAccess }) => {
  if (item.hidden) {
    return false;
  }
  if (item.adminOnly && !isAdmin) {
    return false;
  }
  if (item.alwaysShow) {
    return true;
  }
  return hasPageAccess(item.key);
};

/**
 * Menu sections filtered to what the current user may see; empty sections dropped.
 */
export const getVisibleSections = (ctx) =>
  menuSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => isMenuItemVisible(item, ctx))
    }))
    .filter(section => section.items.length > 0);
